import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, OctagonAlert, Search } from 'lucide-react';
import { DEFECT_REPORTERS, SLOTS, SLOT_LABEL, STATUS_LABEL, formatDay, formatDateTime, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from './util.js';
import { addDays } from './shared.jsx';

// Defects: a completed install that has stopped working, so we go back. The
// office raises one from the job (Raise a defect) or from Bookings (Add
// defect on Operations > Defects: find the job by day, address, job reference
// or NU number). It stays open until marked fixed. Each gets a revisit with no
// date; Book revisit puts it on a team's day once Network Plus has confirmed
// it with the customer (or drag it on the calendar). A revisit is half a slot.
const REPORTED_BY = Object.fromEntries(DEFECT_REPORTERS.map((r) => [r.value, r.label]));

// The form: what stopped working, who told us and when, and a return visit.
export function DefectForm({ booking, onDone, onCancel }) {
  const [description, setDescription] = useState('');
  const [reportedBy, setReportedBy] = useState('');
  const [reportedOn, setReportedOn] = useState(todayUk());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const out = await api(`/bookings/${booking.id}/defects`, { method: 'POST', body: { description, reportedBy, reportedOn } });
      onDone(`Defect raised on ${booking.reference}; revisit ${out.returnReference} is waiting for a date (book it once Network Plus confirms one).`);
    } catch (err) {
      setError(problem(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form defect-form" onSubmit={submit}>
      {error && <p className="notice-line notice-bad" role="alert">{error}</p>}
      <label className="fld">What has stopped working<textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} required autoFocus placeholder="e.g. Meter not reading, leak at the boundary box" /></label>
      <div className="fld-row">
        <label className="fld">
          Reported by
          <select value={reportedBy} onChange={(e) => setReportedBy(e.target.value)} required>
            <option value="">Choose…</option>
            {DEFECT_REPORTERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
        <label className="fld narrow">Reported on<input type="date" value={reportedOn} max={todayUk()} onChange={(e) => setReportedOn(e.target.value)} required /></label>
      </div>
      <p className="small muted">A revisit is made with it, waiting for a date: book it once Network Plus has confirmed a day with the customer. It uses half a slot.</p>
      <div className="form-actions">
        {onCancel && <button type="button" className="btn" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn primary with-icon" disabled={busy || !description.trim() || !reportedBy}><OctagonAlert size={16} aria-hidden="true" /> {busy ? 'Raising…' : 'Raise defect'}</button>
      </div>
    </form>
  );
}

// Marking one fixed: when, and what was done. Fixed before its revisit
// (nobody's been out yet): remove the revisit from the calendar, or keep it.
const PENDING = ['booked', 'assigned'];
function FixForm({ defect, onDone, onCancel }) {
  const [fixedOn, setFixedOn] = useState(todayUk());
  const [fixNote, setFixNote] = useState('');
  const v = defect.returnVisit;
  const pending = Boolean(v) && PENDING.includes(v.status);
  const [revisit, setRevisit] = useState(null); // 'remove' | 'keep'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/bookings/defects/${defect.id}`, { method: 'PUT', body: { fixedOn, fixNote, ...(pending ? { revisit } : {}) } });
      onDone(revisit);
    } catch (err) {
      setError(problem(err));
      setBusy(false);
    }
  };
  return (
    <form className="form defect-fix" onSubmit={submit}>
      {error && <p className="notice-line notice-bad" role="alert">{error}</p>}
      <div className="fld-row">
        <label className="fld">What was done<input value={fixNote} onChange={(e) => setFixNote(e.target.value)} maxLength={2000} placeholder="Optional" /></label>
        <label className="fld narrow">Fixed on<input type="date" value={fixedOn} max={todayUk()} onChange={(e) => setFixedOn(e.target.value)} required /></label>
      </div>
      {pending && (
        <fieldset className="fix-revisit">
          <legend>Fixed before the revisit?</legend>
          <p className="small">
            Revisit {defect.returnReference} is still {v.date ? <>booked for <strong>{formatDay(v.date)}{v.slot ? ` ${SLOT_LABEL[v.slot]}` : ''}</strong>{v.teamName ? ` with ${v.teamName}` : ''}</> : 'waiting for a date'}.
          </p>
          <label className="level-opt"><input type="radio" name={`revisit-${defect.id}`} checked={revisit === 'remove'} onChange={() => setRevisit('remove')} /> <span><strong>Remove the revisit</strong> <span className="muted small">Takes it off the calendar; nobody needs to go.</span></span></label>
          <label className="level-opt"><input type="radio" name={`revisit-${defect.id}`} checked={revisit === 'keep'} onChange={() => setRevisit('keep')} /> <span><strong>Keep the revisit</strong> <span className="muted small">The team still goes, e.g. to check it.</span></span></label>
        </fieldset>
      )}
      <div className="form-actions">
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn primary" disabled={busy || (pending && !revisit)}>Mark fixed</button>
      </div>
    </form>
  );
}

// A defect's revisit: waiting for a date, or booked (and Book revisit /
// Change to put it on a team's day).
export function RevisitLine({ defect, teams = [], canEdit, onDone }) {
  const v = defect.returnVisit;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ date: v?.date || '', teamId: v?.teamId ? String(v.teamId) : '', slot: v?.slot || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!v) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/bookings/${defect.returnBookingId}`, { method: 'PUT', body: { bookingDate: f.date, teamId: f.teamId ? Number(f.teamId) : null, slot: f.slot || null } });
      setEditing(false);
      onDone(`Revisit ${defect.returnReference} booked for ${formatDay(f.date)}.`);
    } catch (err) {
      setError(problem(err));
    } finally {
      setBusy(false);
    }
  };
  const booked = Boolean(v.date);
  return (
    <div className={`revisit-line${booked ? ' booked' : ''}`}>
      <span className="small">
        <strong>Revisit {defect.returnReference}</strong>{': '}
        {booked
          ? <>{formatDay(v.date)}{v.slot ? ` ${SLOT_LABEL[v.slot]}` : ''}{v.teamName ? ` · ${v.teamName}` : ''} · {STATUS_LABEL[v.status]}</>
          : <span className="warn-text">waiting for a date</span>}
      </span>
      {canEdit && defect.status === 'open' && !editing && <button type="button" className="btn" onClick={() => setEditing(true)}>{booked ? 'Change revisit' : 'Book revisit'}</button>}
      {editing && (
        <form className="form defect-fix" onSubmit={save}>
          {error && <p className="notice-line notice-bad" role="alert">{error}</p>}
          <p className="small muted">The day Network Plus confirmed with the customer. It goes on that team&apos;s day as half a slot.</p>
          <div className="fld-row">
            <label className="fld narrow">Date<input type="date" value={f.date} min={todayUk()} onChange={(e) => setF((x) => ({ ...x, date: e.target.value }))} required /></label>
            <label className="fld">
              Team
              <select value={f.teamId} onChange={(e) => setF((x) => ({ ...x, teamId: e.target.value }))} required>
                <option value="">Choose…</option>
                {teams.filter((t) => t.active || String(t.id) === f.teamId).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label className="fld narrow">
              Slot
              <select value={f.slot} onChange={(e) => setF((x) => ({ ...x, slot: e.target.value }))}>
                <option value="">Not set</option>
                {SLOTS.filter((s) => s.value !== 'all_day').map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => setEditing(false)}>Cancel</button>
            <button type="submit" className="btn primary" disabled={busy || !f.date || !f.teamId}>Book revisit</button>
          </div>
        </form>
      )}
    </div>
  );
}

// In the booking pop-up: its defects, and Raise a defect on a completed job.
export function BookingDefects({ booking, teams = [], canEdit, onChanged }) {
  const [list, setList] = useState([]);
  const [raising, setRaising] = useState(false);
  const [fixing, setFixing] = useState(null);
  const load = () => api(`/bookings/${booking.id}/defects`).then((r) => setList(r.defects), () => setList([]));
  useEffect(() => { load(); }, [booking.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const canRaise = canEdit && booking.status === 'completed';
  if (!list.length && !canRaise) return null;
  const done = (text) => { setRaising(false); setFixing(null); load(); onChanged?.(text); };

  return (
    <div className="booking-defects">
      <div className="booking-defects-head">
        <h3>Defects{list.length ? ` (${list.length})` : ''}</h3>
        {canRaise && !raising && <button type="button" className="btn with-icon" onClick={() => setRaising(true)}><OctagonAlert size={16} aria-hidden="true" /> Raise a defect</button>}
      </div>
      {raising && <DefectForm booking={booking} onDone={done} onCancel={() => setRaising(false)} />}
      {list.map((d) => (
        <div key={d.id} className={`defect-card d-${d.status}`}>
          <p>
            <span className={`tag defect-tag d-${d.status}`}>{d.status === 'open' ? 'Open' : 'Fixed'}</span>
            {' '}<strong>{d.description}</strong>
          </p>
          <p className="small muted">
            Reported by {REPORTED_BY[d.reportedBy] || d.reportedBy} on {formatDay(d.reportedOn)} · raised by {d.raisedBy}, {formatDateTime(d.raisedAt)}
          </p>
          <RevisitLine defect={d} teams={teams} canEdit={canEdit} onDone={done} />
          {d.status === 'fixed' && <p className="small">Fixed on {formatDay(d.fixedOn)} by {d.fixedBy}{d.fixNote ? `: ${d.fixNote}` : ''}</p>}
          {d.status === 'open' && canEdit && (fixing === d.id
            ? <FixForm defect={d} onDone={(rv) => done(`Defect on ${booking.reference} marked fixed${rv === 'remove' ? `; revisit ${d.returnReference} removed from the calendar` : rv === 'keep' ? `; revisit ${d.returnReference} kept` : ''}.`)} onCancel={() => setFixing(null)} />
            : <button type="button" className="btn" onClick={() => setFixing(d.id)}>Mark fixed</button>)}
        </div>
      ))}
    </div>
  );
}

// Bookings > Add defect: find the job, then raise it. Pick a day to list the
// jobs completed that day, or search (any day). Only completed jobs can have
// a defect; the others show why not.
export function AddDefect({ onClose, onDone }) {
  const [q, setQ] = useState('');
  const [day, setDay] = useState(todayUk());
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);
  const term = q.trim();
  const searching = term.length >= 2;

  useEffect(() => {
    let live = true;
    const url = searching
      ? `/bookings?q=${encodeURIComponent(term)}`
      : `/bookings?from=${day}&to=${day}&dated=yes&status=completed`;
    const t = setTimeout(() => {
      api(url).then(
        (r) => { if (live) { setResults(r.bookings.slice(0, 50)); setError(null); } },
        (e) => { if (live) setError(problem(e)); },
      );
    }, searching ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [term, searching, day]);

  return (
    <Modal open wide title={picked ? `Raise a defect on ${picked.reference}` : 'Add a defect'} onClose={onClose}>
      {picked ? (
        <div className="stack">
          <p className="small"><strong>{picked.reference}</strong> · {[picked.addressLine1, picked.town, picked.postcode].filter(Boolean).join(', ')}{picked.clientRef ? ` · ${picked.clientRef}` : ''}{picked.bookingDate ? ` · installed ${formatDay(picked.bookingDate)}` : ''}</p>
          <DefectForm booking={picked} onDone={onDone} onCancel={() => setPicked(null)} />
        </div>
      ) : (
        <div className="stack">
          <p className="small muted">Pick the day it was installed to see that day&apos;s completed jobs, or search any day by address, postcode, job reference (Network Plus) or NU number.</p>
          <div className="week-nav defect-day" role="group" aria-label="Day installed">
            <button type="button" className="btn icon-only" aria-label="Previous day" disabled={searching} onClick={() => setDay(addDays(day, -1))}><ChevronLeft size={18} aria-hidden="true" /></button>
            <button type="button" className="btn" disabled={searching} onClick={() => setDay(todayUk())}>Today</button>
            <button type="button" className="btn icon-only" aria-label="Next day" disabled={searching || day >= todayUk()} onClick={() => setDay(addDays(day, 1))}><ChevronRight size={18} aria-hidden="true" /></button>
            <input type="date" className="filter-date" value={day} max={todayUk()} disabled={searching} aria-label="Day installed" onChange={(e) => /^d{4}-d{2}-d{2}$/.test(e.target.value) && setDay(e.target.value)} />
            <span className="week-label">{searching ? 'Searching every day' : formatDay(day)}</span>
          </div>
          <label className="defect-search">
            <Search size={18} aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Or search: e.g. 11 Kenilworth Avenue, 000042284437-0010 or NU-000024" aria-label="Find a job" />
          </label>
          {error && <p className="notice-line notice-bad">{error}</p>}
          {results && !results.length && <p className="muted">{searching ? 'No jobs match.' : `No jobs completed on ${formatDay(day)}.`}</p>}
          {results?.length > 0 && (
            <ul className="defect-results">
              {results.map((b) => {
                const ok = b.status === 'completed';
                return (
                  <li key={b.id}>
                    <button type="button" className="defect-result" disabled={!ok} onClick={() => setPicked(b)}>
                      <span className="defect-result-what">
                        <strong>{b.reference} · {b.addressLine1}</strong>
                        <span className="small muted">{[b.town, b.postcode].filter(Boolean).join(', ')}{b.clientRef ? ` · ${b.clientRef}` : ''}{b.bookingDate ? ` · ${formatDay(b.bookingDate)}` : ''}{b.teamName ? ` · ${b.teamName}` : ''}</span>
                      </span>
                      <span className={`status status-${b.status}`}>{STATUS_LABEL[b.status]}</span>
                      {!ok && <span className="small muted defect-result-why">Only a completed job can have a defect</span>}
                      {b.openDefects > 0 && <span className="small defect-result-why">{b.openDefects} open defect{b.openDefects === 1 ? '' : 's'} already</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
